<template>
  <div>
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-qup-blue to-qup-dark text-white">
      <div class="container-max section-padding">
        <div class="text-center">
          <h1 class="text-4xl md:text-5xl font-bold mb-6">
            Our Consulting Services
          </h1>
          <p class="text-xl text-gray-200 max-w-3xl mx-auto">
            Expert technology consulting services designed to transform your business operations, enhance security, and drive digital innovation.
          </p>
        </div>
      </div>
    </section>

    <!-- Services Detail -->
    <section class="section-padding">
      <div class="container-max">
        <div class="space-y-20">
          <!-- IT Risk & Security Consultancy -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div class="text-5xl mb-6">🔒</div>
              <h2 class="text-3xl font-bold text-qup-dark mb-6">
                IT Risk & Security Consultancy
              </h2>
              <p class="text-lg text-gray-600 mb-6">
                Protect your digital assets and ensure business continuity with our comprehensive security consulting services. We help organizations identify, assess, and mitigate cybersecurity risks while ensuring compliance with industry standards.
              </p>
              
              <div class="space-y-4 mb-8">
                <h3 class="text-xl font-semibold text-qup-dark">What We Offer:</h3>
                <ul class="space-y-3">
                  <li v-for="service in securityServices" :key="service" class="flex items-start">
                    <svg class="w-5 h-5 text-qup-blue mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                    <span class="text-gray-700">{{ service }}</span>
                  </li>
                </ul>
              </div>
              
              <router-link to="/contact" class="btn-primary">
                Get Security Assessment
              </router-link>
            </div>
            
            <div class="bg-gray-50 rounded-lg p-8">
              <h3 class="text-xl font-semibold text-qup-dark mb-4">Security Framework</h3>
              <div class="space-y-4">
                <div class="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                  <span class="font-medium">Risk Assessment</span>
                  <span class="text-qup-blue">✓</span>
                </div>
                <div class="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                  <span class="font-medium">Compliance Audit</span>
                  <span class="text-qup-blue">✓</span>
                </div>
                <div class="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                  <span class="font-medium">Incident Response</span>
                  <span class="text-qup-blue">✓</span>
                </div>
                <div class="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                  <span class="font-medium">Security Training</span>
                  <span class="text-qup-blue">✓</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Process Optimization -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div class="order-2 lg:order-1">
              <div class="bg-gray-50 rounded-lg p-8">
                              <h3 class="text-xl font-semibold text-qup-dark mb-4">Optimization Process</h3>
              <div class="space-y-4">
                <div class="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                  <span class="font-medium">Current State Analysis</span>
                  <span class="text-qup-blue">1</span>
                </div>
                <div class="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                  <span class="font-medium">Process Mapping</span>
                  <span class="text-qup-blue">2</span>
                </div>
                <div class="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                  <span class="font-medium">Optimization Design</span>
                  <span class="text-qup-blue">3</span>
                </div>
                <div class="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                  <span class="font-medium">Implementation</span>
                  <span class="text-qup-blue">4</span>
                </div>
              </div>
              </div>
            </div>
            
            <div class="order-1 lg:order-2">
              <div class="text-5xl mb-6">⚡</div>
              <h2 class="text-3xl font-bold text-qup-dark mb-6">
                Process Optimization
              </h2>
              <p class="text-lg text-gray-600 mb-6">
                Streamline your business operations and improve efficiency with our data-driven process optimization services. We analyze your current workflows and implement improvements that reduce costs, increase productivity, and enhance customer satisfaction.
              </p>
              
              <div class="space-y-4 mb-8">
                <h3 class="text-xl font-semibold text-qup-dark">Our Approach:</h3>
                <ul class="space-y-3">
                  <li v-for="service in optimizationServices" :key="service" class="flex items-start">
                    <svg class="w-5 h-5 text-qup-blue mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                    <span class="text-gray-700">{{ service }}</span>
                  </li>
                </ul>
              </div>
              
              <router-link to="/contact" class="btn-primary">
                Optimize Your Processes
              </router-link>
            </div>
          </div>

          <!-- Software Development & Integration -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div class="text-5xl mb-6">💻</div>
              <h2 class="text-3xl font-bold text-qup-dark mb-6">
                Software Development & Integration
              </h2>
              <p class="text-lg text-gray-600 mb-6">
                Transform your business with custom software solutions and seamless system integration. Our development team creates scalable, secure, and user-friendly applications that address your specific business challenges and drive growth.
              </p>
              
              <div class="space-y-4 mb-8">
                <h3 class="text-xl font-semibold text-qup-dark">Development Services:</h3>
                <ul class="space-y-3">
                  <li v-for="service in developmentServices" :key="service" class="flex items-start">
                    <svg class="w-5 h-5 text-qup-blue mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                    <span class="text-gray-700">{{ service }}</span>
                  </li>
                </ul>
              </div>
              
              <router-link to="/contact" class="btn-primary">
                Start Your Project
              </router-link>
            </div>
            
            <div class="bg-gray-50 rounded-lg p-8">
              <h3 class="text-xl font-semibold text-qup-dark mb-4">Technology Stack</h3>
              <div class="grid grid-cols-2 gap-4">
                <div class="p-4 bg-white rounded-lg shadow-sm text-center">
                  <div class="text-2xl mb-2">🌐</div>
                  <span class="font-medium text-sm">Web Development</span>
                </div>
                <div class="p-4 bg-white rounded-lg shadow-sm text-center">
                  <div class="text-2xl mb-2">📱</div>
                  <span class="font-medium text-sm">Mobile Apps</span>
                </div>
                <div class="p-4 bg-white rounded-lg shadow-sm text-center">
                  <div class="text-2xl mb-2">🔗</div>
                  <span class="font-medium text-sm">API Integration</span>
                </div>
                <div class="p-4 bg-white rounded-lg shadow-sm text-center">
                  <div class="text-2xl mb-2">☁️</div>
                  <span class="font-medium text-sm">Cloud Solutions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section-padding bg-qup-dark text-white">
      <div class="container-max text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-6">
          Ready to Transform Your Business?
        </h2>
        <p class="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
          Let's discuss how our consulting services can help you achieve your business objectives and drive sustainable growth through technology innovation.
        </p>
        <router-link to="/contact" class="btn-secondary">
          Schedule a Consultation
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
const securityServices = [
  'Comprehensive Security Audits & Assessments',
  'Risk Management Framework Development',
  'Compliance & Governance Implementation',
  'Incident Response Planning & Testing',
  'Security Awareness Training Programs',
  'Penetration Testing & Vulnerability Assessment',
  'Security Architecture Design',
  'Business Continuity Planning'
]

const optimizationServices = [
  'Business Process Analysis & Mapping',
  'Workflow Automation & Streamlining',
  'Performance Metrics & KPI Development',
  'Digital Transformation Strategy',
  'Change Management & Training',
  'Continuous Improvement Programs',
  'Technology Integration Planning',
  'Cost Reduction & Efficiency Analysis'
]

const developmentServices = [
  'Custom Software Development',
  'Web & Mobile Application Development',
  'System Integration & API Development',
  'Legacy System Modernization',
  'Database Design & Management',
  'Cloud Migration & Deployment',
  'DevOps & CI/CD Implementation',
  'Software Testing & Quality Assurance'
]
</script>
